import {
  ArrowLeftOutlined,
  CloseOutlined,
  SendOutlined,
} from '@ant-design/icons';
import React, { useState } from 'react';
import { PrimaryButton } from '../../../buttons/PrimaryButton/PrimaryButton';
import { PhoneInput } from '../../../components/PhoneInput/PhoneInput';
import { EmailInput } from '../../InviteUserModal/EmailInput/EmailInput';
import { ReactComponent as Diamond } from './diamond.svg';
import styles from './ContactSalesModal.module.css';
import classNames from 'classnames';
import { useCompanyCRUDActions } from '@mm/state/actions/CompanyCRUDActions';
import { useSelector } from 'react-redux';
import { activeCompanySelector } from '@mm/state/selectors/viewSelectors';
import TextInput from '../../../components/TextInput';
import { selectCurrentUser } from '@mm/state/selectors/userSelector';
import Textarea from '../../../components/Textarea';
import useRouter from 'use-react-router';
import { ContactSalesModalProps } from '@mm/state/modules/modalsState/props';
import { useModalActions } from '@mm/state/modules/modalsState/actions';

export const ContactSalesModal = ({
  onBack,
  withDesiredAudienceInput = false,
}: ContactSalesModalProps) => {
  const company = useSelector(activeCompanySelector);
  const currentUser = useSelector(selectCurrentUser);
  const { closeModal } = useModalActions();

  const [email, setEmail] = useState<
    { email: string; isValid: boolean } | undefined
  >(
    currentUser?.email
      ? { email: currentUser?.email, isValid: true }
      : undefined,
  );
  const [phone, setPhone] = useState<
    { phone: string; isValid: boolean } | undefined
  >(
    company?.phoneNumber
      ? { phone: company?.phoneNumber, isValid: true }
      : undefined,
  );
  const [firstName, setFirstName] = useState(currentUser?.firstName);
  const [targetAudienceDescription, setTargetAudienceDescription] =
    useState('');
  const [showContactForm, setShowContactForm] = useState(true);

  const { location } = useRouter();
  const isInstallationPath = location.pathname.includes('installation');
  const { update } = useCompanyCRUDActions();

  const onSubmit = async () => {
    await update({
      id: company?.id,
      businessFirstName: firstName,
      businessEmail: email?.email,
      phoneNumber: phone?.phone,
      targetAudienceDescription,
      bookAnEnterprise: true,
    });
    setShowContactForm(false);
  };

  return (
    <div
      className={classNames(styles.container, {
        [styles.thankyouContainer]: !showContactForm,
      })}
    >
      <CloseOutlined
        className={styles.closeIcon}
        onClick={() => closeModal()}
      />

      {showContactForm ? (
        <>
          <div className={styles.title}>
            <div className={styles.heading}>Schedule or call me now</div>
            {withDesiredAudienceInput && (
              <div className={styles.subHeading}>
                Connect with our Data Science team to capture over 2,000 unique
                leads from anonymous traffic
              </div>
            )}
          </div>

          <div className={styles.form}>
            <div>
              Name
              <TextInput
                value={firstName}
                onChange={e => setFirstName(e.target.value)}
                className={classNames(styles.input, {
                  [styles.invalidInput]: !firstName,
                })}
              />
            </div>
            <div>
              Contact Phone number
              <PhoneInput
                value={phone?.phone}
                onChange={(val, isValid) => setPhone({ phone: val, isValid })}
                className={styles.input}
              />
            </div>
            <div>
              Contact Email
              <EmailInput
                inputProps={{
                  placeholder: '',
                  style: { minHeight: '32px', paddingLeft: '12px' },
                }}
                defaultValue={email?.email}
                onChange={p => setEmail({ email: p.email, isValid: p.isValid })}
                className={styles.emailInput}
                withIcon={false}
                strict
              />
            </div>
            {withDesiredAudienceInput && (
              <div>
                Describe your desired target audience in detail
                <Textarea
                  className={classNames(styles.input, styles.textarea)}
                  value={targetAudienceDescription}
                  onChange={e => setTargetAudienceDescription(e.target.value)}
                  maxLength={250}
                />
              </div>
            )}
          </div>

          <div className={styles.buttons}>
            <PrimaryButton
              className={styles.button}
              onClick={onSubmit}
              disabled={!email?.isValid || !phone?.isValid || !firstName}
            >
              <SendOutlined />
              Send Request
            </PrimaryButton>

            {onBack && (
              <PrimaryButton className={styles.cancelButton} onClick={onBack}>
                <ArrowLeftOutlined />
                Back to plans
              </PrimaryButton>
            )}
          </div>
        </>
      ) : (
        <>
          <Diamond className={styles.diamond} />
          <div className={styles.title}>
            <div className={styles.heading}>Thank you!</div>
            <div className={styles.subHeading}>
              A member of our Enterprise team will reach out shortly.
            </div>
          </div>
          {isInstallationPath && (
            <div className={styles.box}>
              <div className={styles.note}>
                We have also <strong>activated a FREE trial </strong>for you to
                see the benefits of Customers.ai X-Ray Pixel immediately.
              </div>
              <PrimaryButton
                className={styles.primaryButton}
                onClick={() => closeModal()}
              >
                Got it!
              </PrimaryButton>
            </div>
          )}
        </>
      )}
    </div>
  );
};
